<div *ngIf="selectedEvent && selectedEvent.published">
  <div class="max-w-6xl mx-auto px-4 py-8">
    <div class="flex flex-wrap -mx-4">
      <!-- Image Section -->
      <div class="w-full md:w-2/5 px-4 mb-8">
        <div
          class="rounded-lg shadow-lg overflow-hidden bg-gray-50 mb-5"
          style="aspect-ratio: 4/3; max-height: 480px"
        >
          <img
            nz-image
            nzDisablePreview
            nzFallback="assets/placeholder.jpg"
            [nzSrc]="eventImageUrl"
            [alt]="selectedEvent.name"
            class="w-full h-full object-contain"
          />
        </div>
        <div style="height: 40px"></div>
        <p
          class="text-gray-900 text-lg leading-8 font-medium mb-4"
          *ngIf="selectedEvent.onlineShopPresent && selectedEvent.shopLink"
        >
          Event Store
        </p>

        <a
          *ngIf="selectedEvent.onlineShopPresent && selectedEvent.shopLink"
          [href]="selectedEvent.shopLink"
          target="_blank"
          rel="noopener noreferrer"
          class="flex justify-center mt-4"
        >
          <button
            class="flex justify-center items-center gap-2 w-full bg-black text-white px-6 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 font-semibold"
          >
            <span
              nz-icon
              nzType="shop"
              nzTheme="outline"
              class="text-lg"
            ></span>
            Go to Event Store
          </button>
        </a>
      </div>

      <!-- Content Section -->
      <div class="w-full md:flex-1 px-4">
        <div
          *ngIf="statusBanner as banner"
          class="flex items-start gap-3 p-4 mb-4 rounded-md border border-amber-300 bg-amber-50 text-amber-900"
          role="alert"
        >
          <span
            nz-icon
            nzType="info-circle"
            nzTheme="outline"
            class="text-xl mt-0.5"
          ></span>
          <div>
            <p class="font-semibold leading-snug">{{ banner.title }}</p>
            <p class="text-sm leading-snug">{{ banner.message }}</p>
          </div>
        </div>

        <h2 class="text-3xl font-bold mb-2">{{ selectedEvent?.name }}</h2>
        <p class="text-lg font-medium leading-8 text-orange-700 mb-4">
          {{ selectedEvent?.eventType?.name }}
        </p>

        <!-- Event Date, Time, and Location -->
        <div class="text-gray-500 mb-4">
          <p class="text-md font-semibold">
            <span nz-icon nzType="calendar" class="mr-1"></span>
            {{ selectedEvent?.date | date: 'mediumDate' }}
          </p>
          <p class="text-md font-semibold">
            <span nz-icon nzType="clock-circle" class="mr-1"></span>
            {{ selectedEvent?.time }}
          </p>
          <p class="text-md font-semibold">
            <span nz-icon nzType="environment" class="mr-1"></span>
            {{ selectedEvent?.location?.description }}
          </p>
        </div>

        <!-- Event Details -->
        <div
          class="text-gray-700 mb-6 prose max-w-none"
          [innerHTML]="safeDetails"
        ></div>

        <hypenation-tickets-seat-map-picker
          *ngIf="seatMap && !ticketsAreLoading && eventTickets?.length"
          [seatMap]="seatMap"
          [selectedTicketTypeUid]="selectedTicketType?.uid"
          [pickableTicketTypeUids]="addableTicketTypeUids"
          (bayPicked)="onBayPicked($event)"
          (proceed)="scrollToTickets()"
        >
        </hypenation-tickets-seat-map-picker>

        <!-- Held back until the tickets AND the seat map have settled: the map is inserted above this
             heading, so showing it first would push it, and the list under it, down the page. -->
        <p
          *ngIf="!ticketsAreLoading"
          id="tickets-heading"
          tabindex="-1"
          class="text-gray-900 text-lg leading-8 font-medium mb-4 scroll-mt-20 outline-none"
        >
          Tickets
        </p>

        <hypenation-tickets-ticket-selection
          [eventStatus]="selectedEvent.status"
          *ngIf="!ticketsAreLoading && eventTickets.length > 0"
          [ticketTypes]="eventTickets"
          [selectedTicket]="selectedTicketType"
          (ticketChange)="onTicketSelected($event)"
        >
        </hypenation-tickets-ticket-selection>

        <p *ngIf="!ticketsAreLoading && eventTickets.length === 0" class="italic">
          No Tickets found
        </p>

        <nz-spin *ngIf="ticketsAreLoading" nzSimple [nzSize]="'large'"></nz-spin>

        <nz-spin *ngIf="loadingAddOns" nzSimple [nzSize]="'small'"></nz-spin>

        <section
          *ngIf="!loadingAddOns && selectedTicketType && hasConfiguredAddOns"
          class="w-full max-w-lg my-4 border border-gray-200 rounded-lg p-4 bg-gray-50"
        >
          <div class="flex items-center justify-between gap-3">
            <div>
              <p class="text-gray-900 text-sm font-semibold mb-1">Add-ons</p>
              <p class="text-xs text-gray-600 mb-0">
                {{ selectedAddOnsCount }} selected
                <ng-container *ngIf="hasSelectedAddOns">
                  ·
                  {{
                    selectedAddOnsTotal
                      | currency : selectedAddOnsCurrencyCode
                  }}
                </ng-container>
              </p>
            </div>
            <button
              nz-button
              nzType="default"
              nzSize="small"
              type="button"
              (click)="openAddOnsModal()"
            >
              {{ hasSelectedAddOns ? 'Edit add-ons' : 'Choose add-ons' }}
            </button>
          </div>
          <ng-container *ngIf="false">
            <label
              *ngFor="let addOn of ticketTypeAddOns"
              nz-checkbox
              [ngModel]="isAddOnSelected(addOn)"
              [ngModelOptions]="{ standalone: true }"
              [nzDisabled]="addOn.required"
              (ngModelChange)="onAddOnToggle(addOn, $event)"
              class="flex items-start"
            >
              <span class="ml-2">
                <span class="block text-sm font-medium text-gray-900">
                  {{ addOn.name }}
                  <span *ngIf="addOn.required" class="text-xs text-orange-700">
                    Required
                  </span>
                </span>
                <span class="block text-xs text-gray-600">
                  {{ addOn.amount | currency : addOn.currencyCode }}
                  <ng-container *ngIf="addOn.description">
                    · {{ addOn.description }}
                  </ng-container>
                </span>
              </span>
            </label>
          </ng-container>
          <div
            *ngIf="hasSelectedAddOns"
            class="mt-3 flex justify-between text-sm font-semibold border-t border-gray-200 pt-3"
          >
            <span>Ticket total</span>
            <span>{{
              selectedTicketTotal | currency : selectedTicketTotalCurrencyCode
            }}</span>
          </div>
        </section>

        <section *ngIf="fields.length === 0">
          <p class="text-gray-900 text-lg leading-8 font-medium my-2">
            Number Of Tickets
          </p>

          <hypenation-tickets-quantity-component
            [(numberOfTickets)]="numberOfTickets"
            [disabled]="isHacApp || hasSelectedAddOns"
          ></hypenation-tickets-quantity-component>
          <p *ngIf="hasSelectedAddOns" class="mt-2 text-xs text-gray-500">
            Add-ons can only be purchased with one ticket.
          </p>
        </section>

        @if (selectedEvent.status === "OPEN") {
        <div>
          <div class="my-4" *ngIf="fields.length > 0">
            <p>
              <span nz-icon nzType="info-circle" nzTheme="outline"></span>
              <span class="text-xs text-gray-400"
                >The organizers of this event require additional information for
                each ticket. To buy multiple tickets, please add one ticket to
                your cart at a time and enter the required details before adding
                another ticket.</span
              >
            </p>
          </div>

          <p *ngIf="selectedEvent.requiresIndemnity">
            <label nz-checkbox [(ngModel)]="requiresIndemnity"
              >I Agree and Sign the Indemnity.
              <span
                (click)="viewIndemity($event)"
                class="underline font-semibold cursor-pointer"
                >(Show Indemnity)</span
              >
            </label>
          </p>
          <div class="flex flex-col space-y-2 my-6">
            <div class="flex space-x-4">
              <button
                [disabled]="
                  loadingCart ||
                  (selectedEvent.requiresIndemnity && !requiresIndemnity) ||
                  (isHacApp && isProductInCart) ||
                  selectedTicketBlocked
                "
                (click)="addToCart()"
                [ngClass]="
                  (!loadingTickets && eventTickets.length === 0) ||
                  loadingCart ||
                  (selectedEvent.requiresIndemnity && !requiresIndemnity) ||
                  (isHacApp && isProductInCart) ||
                  selectedTicketBlocked
                    ? 'cursor-not-allowed bg-gray-200 hover:bg-gray-200'
                    : ''
                "
                class="bg-black flex gap-2 items-center text-white px-6 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                <img
                  *ngIf="loadingAddtocart || loadingCart"
                  src="assets/loading.gif"
                  alt="loading"
                  class="ml-2 h-6"
                />
                <span
                  *ngIf="!loadingAddtocart && !loadingCart"
                  nz-icon
                  nzType="shopping-cart"
                  nzTheme="outline"
                  class="text-lg"
                ></span>
                Add to Cart
              </button>
            </div>
            <p
              *ngIf="isHacApp && isProductInCart"
              class="text-sm text-orange-600 font-medium"
            >
              As a member, you can only buy 1 ticket per event. This ticket is
              already in your cart.
            </p>
          </div>
        </div>

        }
      </div>
    </div>
  </div>
</div>

<nz-modal
  [(nzVisible)]="addOnsModalVisible"
  [nzTitle]="'Ticket add-ons'"
  [nzOkText]="'Done'"
  [nzCancelText]="'Close'"
  [nzWidth]="640"
  (nzOnOk)="closeAddOnsModal()"
  (nzOnCancel)="closeAddOnsModal()"
>
  <ng-container *nzModalContent>
    <div class="flex flex-col gap-4">
      <p class="text-sm text-gray-600 mb-0">
        Select the extras you want with this ticket. Add-ons can only be
        purchased with one ticket.
      </p>

      <div class="max-h-[58vh] overflow-y-auto pr-1 space-y-3">
        <label
          *ngFor="let addOn of ticketTypeAddOns"
          nz-checkbox
          [ngModel]="isAddOnSelected(addOn)"
          [ngModelOptions]="{ standalone: true }"
          [nzDisabled]="addOn.required"
          (ngModelChange)="onAddOnToggle(addOn, $event)"
          class="flex items-start rounded-md border border-gray-200 p-3 hover:border-gray-300"
        >
          <span class="ml-2">
            <span class="block text-sm font-medium text-gray-900">
              {{ addOn.name }}
              <span *ngIf="addOn.required" class="text-xs text-orange-700">
                Required
              </span>
            </span>
            <span class="block text-xs text-gray-600">
              {{ addOn.amount | currency : addOn.currencyCode }}
              <ng-container *ngIf="addOn.description">
                · {{ addOn.description }}
              </ng-container>
            </span>
          </span>
        </label>
      </div>

      <div
        class="flex justify-between rounded-md bg-gray-50 px-3 py-2 text-sm font-semibold"
      >
        <span>Ticket total</span>
        <span>{{
          selectedTicketTotal | currency : selectedTicketTotalCurrencyCode
        }}</span>
      </div>
    </div>
  </ng-container>
</nz-modal>

<div
  *ngIf="invalidShareLink"
  class="max-w-2xl mx-auto px-4 py-16 text-center"
>
  <span
    nz-icon
    nzType="link"
    nzTheme="outline"
    class="text-5xl text-gray-400 block mb-4"
  ></span>
  <h2 class="text-2xl font-semibold mb-2">Invitation link unavailable</h2>
  <p class="text-gray-600">
    This invitation link is invalid or has been revoked. Please check the link
    you received, or contact the event organiser for a new one.
  </p>
</div>

<div
  *ngIf="selectedEvent && !selectedEvent.published && !invalidShareLink"
  class="max-w-2xl mx-auto px-4 py-16 text-center"
>
  <span
    nz-icon
    nzType="clock-circle"
    nzTheme="outline"
    class="text-5xl text-gray-400 block mb-4"
  ></span>
  <h2 class="text-2xl font-semibold mb-2">This event is not yet available</h2>
  <p class="text-gray-600">
    The organiser is still finalising this event. Please check back later for
    details and ticket sales.
  </p>
</div>

<hypenation-tickets-view-one-skeleton
  *ngIf="!selectedEvent && !invalidShareLink"
></hypenation-tickets-view-one-skeleton>
